<template>
  <div class="patient-list-box" v-loader.opacity="loading">
    <div class="title-box clear">
      <div class="flex-start">
        <button class="btn-back" type="button" name="button" data-test="btnBack" @click="goBack">
          <IconArrowBack/>
          {{ backButtonLabel }}
        </button>

        <h2 class="title">{{ $t('patient.patientList') }}</h2>
      </div>

      <div v-if="tablePatient && !alert.message" class="align-items-center pagination-row">
        <p class="pagination-total">{{ `${tablePatient.total} ${$t("pagination.patients")}` }}</p>
        <BasePagination :total-pages="totalPages" :total="tablePatient.total" :is-loading="loading"
                        :per-page="perPage" :current-page="currentPage" @pagechanged="onPageChange"/>
      </div>
    </div>

    <div class="no-results-box" v-if="alert.message">
      <BaseNoResults :title="alert.title" :text="alert.message"/>
    </div>

    <BaseTable id="patientList" class="no-border" v-if="tablePatient" :tabledata="tablePatient" @item-opened="toDetails">
      <template v-slot:column_lastExamDate="{ item }">
        <p class="last-exam-text" :class="{'text-danger': isLastExamOld(item)}">
          <span>{{ formatLastExamDate(item) }}</span>
          <span class="last-exam-alert" :data-text="$t('patient.lastExamOld')" v-if="isLastExamOld(item)">
            <IconAlertWarning/>
          </span>
        </p>
      </template>

      <template v-slot:column_birthday="{ item }">
        {{ formatBirthday(item) }}
      </template>

      <template v-slot:column_actions="{ item }">
        <div class="flex-center">
          <button class="btn btn-outline-secondary" type="button" name="button" data-test="btnSharePatient" @click.prevent.stop="doShare(item)">
            <IconShare/>
            <span>{{ $t('externalPatient.shareSimple') }}</span>
          </button>
        </div>
      </template>
    </BaseTable>

    <div class="action-bar" v-if="!isSpecialist" @wheel.prevent>
      <button class="btn btn-success" type="button" name="button" data-test="btnNewPatient" @click="newPatient">
        <span>{{ $t('patient.create') }}</span>
      </button>
    </div>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasPatientGeneric'" ref="offcanvasGeneric"/>
      <PatientCreateOffcanvas ref="patientCreateOffcanvas" @patient-created="onPatientCreated"/>
      <BasePatientAccessInfoOffcanvas ref="offcanvasPatientAccessInfo"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.title-box {
  height:                           fit-content;

  .flex-start {
    gap:                            15px;
  }

  .btn-back {
    margin:                         0;
  }
}

.table {
  margin-bottom:                    0 !important;
}

.patient-list-box .no-border {
  padding:                          0 10px;

  .btn-outline-secondary {
    color:                          var(--Shades-00);
    font-size:                      var(--font-size-12);
    text-align:                     right;
    font-weight:                    500;
    line-height:                    normal;
    padding:                        5px 10px;
    text-transform:                 unset;
    letter-spacing:                 unset;
    max-height:                     26px;
    gap:                            6px;
  }
}

.pagination-row {
  margin-left:                      auto;
}

.action-bar {
  position:                         sticky;
  bottom:                           0;
  background:                       var(--Shades-W);
  z-index:                          2;
}

.last-exam-text {
  display:                          flex;
  flex-direction:                   row;
  align-items:                      center;
  gap:                              10px;
  margin:                           0;
  line-height:                      1;

  &.text-danger {
    color:                          var(--Complementary-Orange) !important;
  }
}

.last-exam-alert {
  display:                          flex;
}

:deep(.icon-alert-warning) {
  width:                            16px;
  height:                           16px;
  flex-shrink:                      0;
  position:                         relative;
  // top:                              -1px;
}

@media (min-width: 992px) {
  .patient-list-box {
    display:                        flex;
    flex-direction:                 column;
    flex:                           1;
    min-height:                     0;
  }
}

@media (max-width: 991px) {
  .patient-list-box {
    display:                        flex;
    flex-direction:                 column;

    .no-results-box {
      text-align:                   center;
    }
  }

  .has-alert .patient-list-box {
    :deep(.base-table-root),
    :deep(.inside-box-mob) {
      flex:                         none !important;
      height:                       calc(100dvh - 203px) !important;
      max-height:                   calc(100dvh - 203px) !important;
    }
  }

  .has-usage-bar .patient-list-box {
    :deep(.base-table-root),
    :deep(.inside-box-mob) {
      flex:                         none !important;
      height:                       calc(100dvh - 145px) !important;
      max-height:                   calc(100dvh - 145px) !important;
    }
  }

  .title-box {
    display:                        flex !important;
    justify-content:                flex-start !important;
    gap:                            10px;
    // flex:                           1;
    margin-bottom:                  0 !important;

    .btn-outline-secondary {
      height:                       35px;
      padding:                      10px 12px;

      span {
        display:                    none;
      }
    }

    .title {
      font-size:                    18px !important;
      text-align:                   left;
      // width:                        calc(100% - 44px) !important;
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import PatientCreateOffcanvas from './PatientCreateOffcanvas.vue'

export default {
  name: 'PatientList',
  components: {
    PatientCreateOffcanvas
  },
  data: () => ({
    loading: false,
    alert: {
      title: null,
      message: null,
      class: null
    },
    session: {
      collaborator: {},
      currentClinic: {}
    },
    tablePatient: null,
    perPage: 20,
    currentPage: 1,
    totalPages: 0,
    search: null,
    patientList: {
      items: [],
      total: 0,
      columns: []
    },
    items: {},
    mergedItems: {},
    isSpecialist: null,
    isDataReady: false,
  }),
  computed: {
    backButtonLabel() {
      return this.$utils.getPreviousRouteTitle(this) || this.$i18n.t('goBack')
    },
  },
  mounted() {
    this.$activeLink.value = 'patient'

    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.isSpecialist = this.session.isSpecialist
    this.currentPage = this.session.patientCurrentPage ? this.session.patientCurrentPage : 1;
    this.fetchData()

    this.emitter.on('patient-list-search', this.handleHeaderSearch)
    this.emitter.on('patient-updated', this.fetchData)
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    handleHeaderSearch(value) {
      this.search = value
      this.currentPage = 1
      this.fetchData()
    },
    onPageChange(page) {
      this.currentPage = page;
      this.session.patientCurrentPage = page;
      this.fetchData();
    },
    async fetchData() {
      this.alert = {}

      let params = {
        page: this.currentPage,
        researchFlag: this.session.features.includes('researchFlag')
      }
      if (this.search)
        params.q = this.search

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$patientService.getList(params)

        this.tablePatient = {
          'items': response.result,
          'total': response.totalCount,
          'columns': [
            {name: this.$i18n.t('patient.name'), type: 'string', value: 'rawName', width: '30%'},
            {key: 'lastExamDate', name: this.$i18n.t('patient.lastExamDate'), type: 'custom', value: 'lastExamDate', width: '15%'},
            {name: this.$i18n.t('patient.mrn'), type: 'string', value: 'mrn', width: '15%'},
            ...(!this.isSpecialist ? [{name: this.$i18n.t('document0'), type: 'string', value: 'document0', width: '15%'}] : []),
            {key: 'birthday', name: this.$i18n.t('patient.birthdayDate'), type: 'custom', value: 'birthday', width: '15%'},
            {key: "actions", name: this.$i18n.t('clinic.actions'), type: 'custom', value: 'actions', width: '10%', align: 'text-center'},
          ]
        }

        this.totalPages = Math.ceil(response.totalCount / this.perPage);

        if (response.result.length === 0) {
          this.tablePatient = {};
          this.totalPages = 1
          this.alert = this.$message.alertWithTitle(this, 'request_notfound_patient_title', 'request_notfound_patient_text')
          // this.$message.popup(this, 'request_notfound', 'operationWarning', 'warn')
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      } finally {
        NProgress.done(true)
        this.loading = false
        this.isDataReady = true
      }
    },
    newPatient() {
      this.$refs.patientCreateOffcanvas.show()
    },
    doShare(item) {
      this.$refs.offcanvasPatientAccessInfo.show({ patient: item })
    },
    formatLastExamDate(item) {
      if (!item.lastExamDate)
        return ''

      return moment(item.lastExamDate).format(this.$i18n.t('dateTimeFormat'))
    },
    isLastExamOld(item) {
      if (!item.lastExamDate)
        return false

      return moment().diff(moment(item.lastExamDate), 'years') >= 1
    },
    formatBirthday(item) {
      if (!item.birthday)
        return ''

      return moment.utc(item.birthday).format(this.$i18n.t('dateFormat'))
    },
    onPatientCreated() {
      this.fetchData()
    },
    toDetails(values) {
      if (!values) {
        return
      }

      let item = values.item

      if (!item) {
        return
      }

      this.emitter.emit('open-patient-details', {patientId: item.id})
    },
  },
  unmounted() {
    this.emitter.off('patient-list-search', this.handleHeaderSearch)
    this.emitter.off('patient-updated', this.fetchData)
  },
}
</script>
